import { Headphones } from 'lucide-react';
import { useMusicGate } from '@/context/MusicGateContext';

export default function MusicPage() {
    const { unlocked } = useMusicGate();
    return (
        <div data-testid="music-page" className="min-h-screen bg-brand-bg pt-24">
            {/* Header */}
            <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <p className="font-mono text-xs text-brand-primary uppercase tracking-widest mb-4">DISCOGRAPHY</p>
                    <h1 className="font-syne font-extrabold text-5xl md:text-7xl text-white tracking-tighter uppercase mb-6">
                        MUSIC
                    </h1>
                    <p className="font-space text-slate-400 text-lg max-w-2xl">
                        Stream the latest releases and explore the full catalog on your favorite platform.
                    </p>
                </div>
            </section>

            {/* Streaming Platforms */}
            <section className="py-12 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <h2 className="font-syne font-bold text-2xl text-white mb-8">STREAM NOW</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Spotify */}
                        <a 
                            href="https://open.spotify.com/artist/4hlqSBewRVVJCTwJ7JOj70"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="spotify-link"
                            className="group flex items-center gap-6 p-8 bg-brand-surface border border-white/5 hover:border-white/20 transition-colors duration-500"
                        >
                            <div className="w-16 h-16 rounded-full bg-[#1DB954] flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-syne font-bold text-white text-xl mb-1">Spotify</h3>
                                <p className="font-space text-slate-400">Listen on Spotify</p>
                            </div>
                        </a>

                        {/* Apple Music */}
                        <a 
                            href="https://music.apple.com/us/artist/faded-keenan/1671435655"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="apple-music-link"
                            className="group flex items-center gap-6 p-8 bg-brand-surface border border-white/5 hover:border-white/20 transition-colors duration-500"
                        >
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center">
                                <Headphones className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h3 className="font-syne font-bold text-white text-xl mb-1">Apple Music</h3>
                                <p className="font-space text-slate-400">Listen on Apple Music</p>
                            </div>
                        </a>

                        {/* SoundCloud */}
                        <a 
                            href="https://on.soundcloud.com/qRK3E5wDv4LUqC6zKF"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="soundcloud-link"
                            className="group flex items-center gap-6 p-8 bg-brand-surface border border-white/5 hover:border-white/20 transition-colors duration-500"
                        >
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                                <Headphones className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h3 className="font-syne font-bold text-white text-xl mb-1">SoundCloud</h3>
                                <p className="font-space text-slate-400">Listen on SoundCloud</p>
                            </div>
                        </a>
                    </div>
                </div>
            </section>

            {/* Featured Release */}
            <section className="py-12 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <h2 className="font-syne font-bold text-2xl text-white mb-8">LATEST RELEASE</h2>
                    
                    {/* Spotify Embed */}
                    <div className="music-embed-container mb-8" data-testid="spotify-player-section">
                        <iframe 
                            style={{ borderRadius: '12px' }}
                            src="https://open.spotify.com/embed/artist/4hlqSBewRVVJCTwJ7JOj70?utm_source=generator&theme=0"
                            width="100%" 
                            height="352" 
                            frameBorder="0" 
                            allowFullScreen="" 
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                            loading="lazy"
                            title="Spotify Player"
                        />
                    </div>

                    {/* SoundCloud Embed (mounted only after autoplay unlock) */}
                    <div className="music-embed-container mb-8" data-testid="soundcloud-player-section">
                        {unlocked && (
                            <iframe
                                width="100%"
                                height="300"
                                scrolling="no"
                                frameBorder="no"
                                allow="autoplay"
                                src="https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/tizzlestillfaded&color=%2394A3B8&auto_play=true&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true&shuffle=true"
                                title="SoundCloud Player"
                            />
                        )}
                    </div>

                    {/* Apple Music Embed */}
                    <div className="music-embed-container" data-testid="apple-music-player-section">
                        <iframe 
                            allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write" 
                            frameBorder="0" 
                            height="450" 
                            style={{ width: '100%', overflow: 'hidden', borderRadius: '10px', background: 'transparent' }}
                            sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" 
                            src="https://embed.music.apple.com/us/artist/faded-keenan/1671435655"
                            title="Apple Music Player"
                        />
                    </div>
                </div>
            </section>

            {/* Music Videos / Visualizers */}
            <section className="py-12 pb-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <h2 className="font-syne font-bold text-2xl text-white mb-8">VISUALS</h2>
                    <div className="space-y-8">
                        {/* Featured: No Sleep */}
                        <div className="aspect-video bg-brand-surface border border-white/5 overflow-hidden" data-testid="music-video-1">
                            <iframe
                                width="100%"
                                height="100%"
                                src="https://www.youtube.com/embed/5A05lLB33Zk"
                                title="Faded Keenan - No Sleep"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        </div>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="aspect-video bg-brand-surface border border-white/5 overflow-hidden" data-testid="music-video-2">
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src="https://www.youtube.com/embed/u9lkt4YcXpI"
                                    title="Faded Keenan Music Video"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                />
                            </div>
                            <div className="aspect-video bg-brand-surface border border-white/5 overflow-hidden" data-testid="music-video-3">
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src="https://www.youtube.com/embed/4etCs321F0s"
                                    title="Faded Keenan Music Video"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
